import { type ReactNode } from 'react';
import { Avatar } from '@/components/ui/avatar';
import { MemberNameWithScar } from '@/components/ui/member-name-with-scar';
import type { RaceClaim, RaceCycleType } from '../hooks/use-bonus-history';

const TD = 'py-3 align-middle';

const CYCLE_LABEL: Record<RaceCycleType, string> = {
  daily: 'Diario',
  weekly: 'Semanal',
  monthly: 'Mensual',
};

function fmtDate(d: string): string {
  return d.slice(5, 10).replace('-', '/');
}

function CycleBadge({ cycle }: { cycle: RaceCycleType }): ReactNode {
  return (
    <span className="rounded-md bg-white/[0.04] px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-text-secondary">
      {CYCLE_LABEL[cycle]}
    </span>
  );
}

type WinnerProps = { claim: RaceClaim; isMe: boolean };

function WinnerName({ claim, isMe }: WinnerProps): ReactNode {
  const cls = isMe ? 'font-medium text-brand-red' : 'text-text-primary';
  return (
    <span className={`text-xs ${cls}`}>
      {claim.winner?.first_name}
      {isMe && <span className="ml-1 text-[10px] font-normal">· Tú</span>}
    </span>
  );
}

function WinnerCell({ claim, isMe }: WinnerProps): ReactNode {
  if (!claim.winner)
    return <span className="text-[11px] text-text-tertiary/40">—</span>;
  const redeemed =
    !!claim.winner.has_used_redemption && !!claim.winner.redemption_passed_at;
  return (
    <div className="flex items-center gap-sm">
      <Avatar
        size="xxs"
        name={claim.winner.first_name}
        src={claim.winner.photo}
      />
      <MemberNameWithScar
        hasRedeemed={redeemed}
        name={<WinnerName claim={claim} isMe={isMe} />}
      />
    </div>
  );
}

function Status({ paid }: { paid: boolean }): ReactNode {
  if (paid)
    return <span className="text-[11px] text-text-tertiary">Pagado</span>;
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-text-secondary">
      <span className="h-1.5 w-1.5 rounded-full bg-brand-red" />
      Pendiente
    </span>
  );
}

function DateCell({ c }: { c: RaceClaim }): ReactNode {
  return (
    <td className={`${TD} pr-md text-[11px] tabular-nums text-text-tertiary`}>
      {fmtDate(c.cycle_start)}–{fmtDate(c.cycle_end)}
    </td>
  );
}

function AmountCell({ c }: { c: RaceClaim }): ReactNode {
  return (
    <td
      className={`${TD} pr-md font-mono text-xs font-medium tabular-nums text-text-primary`}
    >
      ${c.amount_eur}
    </td>
  );
}

export function RaceHistoryRow({
  c,
  userId,
}: {
  c: RaceClaim;
  userId: string;
}): ReactNode {
  const isMe = c.winner?.id === userId;
  return (
    <tr className="border-t border-white/[0.06]">
      <td className={`${TD} pr-md`}>
        <CycleBadge cycle={c.cycle_type} />
      </td>
      <DateCell c={c} />
      <td className={`${TD} pr-md`}>
        <WinnerCell claim={c} isMe={isMe} />
      </td>
      <AmountCell c={c} />
      <td className={TD}>
        <Status paid={!!c.paid_in_payment} />
      </td>
    </tr>
  );
}
